import { HttpErrorResponse, HttpStatusCode } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { ErrorLoggingService } from '@services/error-logging.service';
import { Observable, retry, throwError, timer } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class RequestRetryService {
  private errorLoggingService = inject(ErrorLoggingService);

  /**
   * Retries a failed HTTP request with exponential backoff strategy.
   *
   * Only retries requests that fail with retryable status codes (network errors, timeouts, rate limits, and certain
   * server errors). Non-retryable errors are logged and thrown immediately.
   *
   * @param {Observable<T>} source the source observable to retry on failure
   * @param {string} errorMessage custom error message for logging purposes
   * @param {object} context additional context information for error logging
   * @param {number} maxRetries maximum number of retry attempts
   *
   * @returns {Observable<T>} an observable that emits the same values as the source
   */
  public retry<T>(
    source: Observable<T>,
    errorMessage: string,
    context?: object,
    maxRetries: number = 4,
  ): Observable<T> {
    return source.pipe(
      retry({
        count: maxRetries,
        delay: (error: any, retryCount: number) => {
          if (this.shouldRetryRequest(error?.status)) {
            this.errorLoggingService.logHttpError(error, 'Triggered retry', context);

            return timer(Math.pow(retryCount, 2) * 1000);
          }

          if (error instanceof HttpErrorResponse) {
            this.errorLoggingService.logHttpError(error, errorMessage, context);
          }

          return throwError(() => error);
        },
      }),
    );
  }

  /**
   * Determines if an HTTP request should be retried based on its status code.
   *
   * @param {number} statusCode the HTTP status code to evaluate
   *
   * @returns {boolean} true if the request should be retried, false otherwise
   */
  private shouldRetryRequest(statusCode: number): boolean {
    return (
      statusCode === 0 ||
      statusCode === HttpStatusCode.RequestTimeout ||
      statusCode === HttpStatusCode.TooManyRequests ||
      (statusCode >= HttpStatusCode.InternalServerError &&
        statusCode < 600 &&
        ![
          HttpStatusCode.NotImplemented,
          HttpStatusCode.HttpVersionNotSupported,
          HttpStatusCode.VariantAlsoNegotiates,
        ].includes(statusCode))
    );
  }
}
